import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import api from '../services/api';

const ProjectContext = createContext();

export const ProjectProvider = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const [projects, setProjects] = useState([]);
  const [workspaces, setWorkspaces] = useState([]);
  const [activeProjectId, setActiveProjectId] = useState(() => {
    return localStorage.getItem('viewgate_active_project_id');
  });
  const [activeWorkspaceId, setActiveWorkspaceId] = useState(() => {
    return localStorage.getItem('viewgate_active_workspace_id');
  });
  const [loading, setLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const fetchProjects = async () => {
    if (!user) return;
    try {
      // Parallel fetch for projects and workspaces
      const [projRes, wsRes] = await Promise.all([
        api.get('/teams'),
        api.get('/workspaces')
      ]);

      const projectsData = projRes.data?.data || projRes.data || [];
      const workspacesData = wsRes.data || [];
      
      setProjects(projectsData);
      setWorkspaces(workspacesData);
      
      // AUTO-SELECTION DISCOVERY (Point: Zero-Config entry)
      // If no project is persisted in localStorage, use the discovery suggestion from the user object
      const hasStoredProject = localStorage.getItem('viewgate_active_project_id');
      if (!hasStoredProject && user?.discovery?.projectId) {
        setActiveProjectId(String(user.discovery.projectId));
        setActiveWorkspaceId(String(user.discovery.workspaceId));
      }

    } catch (err) {
      /* Projects fetch failed silently for production */
    } finally {
      setLoading(false);
      setIsInitialized(true);
    }
  };

  useEffect(() => {
    if (user) {
      setLoading(true);
      fetchProjects();
    } else if (!authLoading) {
      setProjects([]);
      setActiveProjectId(null);
      setActiveWorkspaceId(null);
      setLoading(false);
    }
  }, [user, authLoading]);

  useEffect(() => {
    if (activeProjectId) {
      localStorage.setItem('viewgate_active_project_id', activeProjectId);
    } else {
      localStorage.removeItem('viewgate_active_project_id');
    }
  }, [activeProjectId]);

  useEffect(() => {
    if (activeWorkspaceId) {
      localStorage.setItem('viewgate_active_workspace_id', activeWorkspaceId);
    } else {
      localStorage.removeItem('viewgate_active_workspace_id');
    }
  }, [activeWorkspaceId]);

  const socketRef = useRef(null);

  // Real-time Workspace Refinement
  useEffect(() => {
    if (!user || !activeWorkspaceId || socketRef.current) return;

    const socketUrl = import.meta.env.VITE_SOCKET_URL || 'http://localhost:4000';
    const socket = io(socketUrl, {
      auth: { token: localStorage.getItem('token') },
      transports: ['websocket', 'polling'], 
      reconnectionAttempts: 3,
      timeout: 10000
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      socket.emit('join-workspace', activeWorkspaceId);
    });

    socket.on('workspace-upgraded', (data) => {
      fetchProjects();
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.off();
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [user, activeWorkspaceId]);

  // Unified Synchronization Effect: URL -> Project -> Workspace
  useEffect(() => {
    if (!isInitialized || projects.length === 0) return;

    const params = new URLSearchParams(location.search);
    const urlProjectId = params.get('projectId');

    // Case 1: URL has a Project ID - High Priority Source of Truth
    if (urlProjectId) {
      const found = projects.find(p => String(p._id) === String(urlProjectId));
      if (found) {
        // Only switch via URL if it matches the active workspace or we are intentionally deep-linking
        // Here we prioritize the deep link project if it's different from the current one
        if (String(urlProjectId) !== String(activeProjectId)) {
          setActiveProjectId(urlProjectId);
          setActiveWorkspaceId(String(found.workspaceId));
          return;
        }
      }
    } 
    
    // Case 2: URL has NO Project ID (or matches current), enforce Workspace integrity
    if (activeWorkspaceId) {
      const currentProj = projects.find(p => String(p._id) === String(activeProjectId));

      // Integrity check: If current project doesn't belong to active workspace, pick the first one
      if (!currentProj || String(currentProj.workspaceId) !== String(activeWorkspaceId)) {
        const firstInWs = projects.find(p => String(p.workspaceId) === String(activeWorkspaceId));
        if (firstInWs) {
          setActiveProjectId(String(firstInWs._id));
        } else if (activeProjectId !== null) {
          setActiveProjectId(null);
        }
      }
    }
  }, [activeWorkspaceId, isInitialized, projects, location.search]); // Removed activeProjectId to break circles

  const filteredProjects = useMemo(() => {
    if (!activeWorkspaceId) return projects;
    return projects.filter(p => p.workspaceId === activeWorkspaceId);
  }, [projects, activeWorkspaceId]);

  const activeProject = useMemo(() => {
    return projects.find(p => p._id === activeProjectId) || null;
  }, [projects, activeProjectId]);

  const activeWorkspace = useMemo(() => {
    return workspaces.find(w => w._id === activeWorkspaceId) || null;
  }, [workspaces, activeWorkspaceId]);

  const userRole = useMemo(() => {
    if (!user || !activeProject) return null;
    
    const userEmail = user.email?.toLowerCase();
    
    if (userEmail && activeProject.ownerEmail) {
      if (activeProject.ownerEmail.toLowerCase() === userEmail) {
        return 'pm';
      }
    }

    if (userEmail && activeProject.members) {
      const memberRecord = activeProject.members.find(m => m.email?.toLowerCase() === userEmail);
      if (memberRecord) return memberRecord.role || 'dev';
    }

    return 'dev';
  }, [user, activeProject]);

  const canAccess = useCallback((path) => {
    if (!isInitialized || loading) return true;
    if (path === '/projects' || path === '/subscription' || path === '/components') return true;
    
    if (projects.length === 0) return path === '/projects' || path === '/subscription' || path === '/components';
    
    if (userRole === 'pm') return true;

    if (userRole === 'ux_ui') {
      const uxAllowed = ['/', '/backlog', '/history', '/board', '/regression-tracking', '/components', '/settings'];
      return uxAllowed.includes(path);
    }
    
    if (!activeProject && projects.length > 0) {
        if (path === '/' || path === '/projects' || path === '/subscription') return true;
        return true; 
    }

    if (userRole === 'client') {
      const clientAllowed = ['/', '/projects', '/backlog', '/regression-tracking', '/components'];
      return clientAllowed.includes(path);
    }

    const allowedPaths = ['/', '/backlog', '/history', '/board', '/regression-tracking', '/components'];
    if (userRole === 'dev' || userRole === 'qa') {
      allowedPaths.push('/settings');
    }
    
    return allowedPaths.includes(path);
  }, [loading, projects.length, userRole, activeProject]);

  const value = {
    projects,
    filteredProjects,
    workspaces,
    activeWorkspace,
    activeWorkspaceId,
    setActiveWorkspaceId,
    activeProject,
    activeProjectId,
    setActiveProjectId,
    userRole,
    canAccess,
    refreshProjects: fetchProjects,
    loading,
    isInitialized
  };

  return (
    <ProjectContext.Provider value={value}>
      {children}
    </ProjectContext.Provider>
  );
};

export const useProject = () => {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('useProject must be used within a ProjectProvider');
  }
  return context;
};
